import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import { customEmailNFValidator } from 'src/app/shared/custom-validators/custom-email.validator';
import { User } from 'src/app/shared/models/user.model';
import { UserService } from 'src/app/shared/services/user.service';
import { MessageDisplayModel } from 'src/app/shared/message/messageDisplay.model';
import { MessageDisplayService } from 'src/app/shared/services/message-display.service';
import { PermissionService } from 'src/app/shared/services/permission.service';
import { Permissions } from 'src/app/shared/models/permissions.model';
import { Subscription } from 'rxjs';
import { AdminRolesCompaniesService } from 'src/app/shared/services/admin-roles-companies.service';
import { Router } from '@angular/router';
import { SessionStorageService } from 'src/app/shared/services/session-storage.service';
import { OAuthUserInfo } from 'src/app/shared/models/oauthuserinfo.model';
import { COUNTRIES } from 'src/utilities/constants/misc.constants';
import { MatDialog } from '@angular/material/dialog';
import { FederatedHelpDialogComponent } from '../federated-help-dialog/federated-help-dialog.component';
import { FIRST_LAST_PATTERN } from 'src/utilities/constants/first-last-name.regex';
import { phoneNumberNFValidator } from 'src/app/shared/custom-validators/phone-number.validator';
import { InternationalPhonePipe } from 'src/app/shared/pipes/international-phone.pipe';
import { NoSpaceIntPhonePipe } from 'src/app/shared/pipes/no-space-int-phone.pipe';
import { LockUserHelpDialogComponent } from '../lock-user-help-dialog/lock-user-help-dialog.component';
import { CompareUserDialogComponent } from '../compare-user-dialog/compare-user-dialog.component';
import {CompanyRole} from '../../../shared/models/company-role.model';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent implements OnInit, OnDestroy {
  @Input() uuid: string;

  // this is the logged in user's info
  userId: string;
  userCompanyId: string;
  roleName: string;

  pageTitle: string;
  user: User;
  hasUserFormBeenSubmitted = false;
  formIsWaiting = false;

  isLocked = false;

  userFormMessage: MessageDisplayModel = new MessageDisplayModel();

  rolesMap = {};
  roles: string[] = [];

  companiesMap = {};
  companies: string[] = [];

  countries = COUNTRIES;
  countryFlags = this.countries.map((e) => e.flag);
  countryCodes = this.countries.map((e) => e.code);
  countryNames = this.countries.map((e) => e.name);

  defaultWorkPhoneFlag: string;
  defaultMobilePhoneFlag: string;

  workPhoneCode: string;
  mobilePhoneCode: string;

  fullWorkPhone = '';
  fullMobilePhone = '';

  permissionsSubscription: Subscription;
  canViewCompanies = false;

  input = {
    selectedRole: '',
    selectedCompany: '',
    emailString: '',
    fullName: '',
    jobTitle: '',
    workPhoneCode: '',
    workPhone: '',
    mobilePhoneCode: '',
    mobilePhone: '',
    federated: false,
  };

  formHasError = false;

  formError = {
    selectedRole: false,
    selectedCompany: false,
    emailString: false,
    jobTitle: false,
    fullName: false,
    workPhone: false,
    mobilePhone: false,
    workPhoneValid: false,
    mobilePhoneValid: false,
  };

  constructor(
    private userService: UserService,
    private router: Router,
    private messageDisplay: MessageDisplayService,
    private permissions: PermissionService,
    private adminRolesCompanies: AdminRolesCompaniesService,
    private sessionStorage: SessionStorageService,
    public dialog: MatDialog,
    private intPhonePipe: InternationalPhonePipe,
    private noSpaceIntPhonePipe: NoSpaceIntPhonePipe
  ) {
    this.permissionsSubscription = this.permissions.permissions$.subscribe(
      (result: Permissions) => {
        if (permissions) {
          this.canViewCompanies = result.canCRUDMasterAdmin;
          this.roleName = result.roleName;
        }
      }
    );

    this.sessionStorage.userInfosChange.subscribe((userInfo: string) => {
      if (userInfo) {
        const user: OAuthUserInfo = JSON.parse(userInfo);
        this.userId = user.sub;
        if (Array.isArray(user.arvCompanyID)) {
          this.userCompanyId = user.arvCompanyID[0];
        } else {
          this.userCompanyId = user.arvCompanyID;
        }
      }
    });

    this.adminRolesCompanies.rolesMap$.subscribe((rolesMap: any) => {
      if (rolesMap) {
        this.rolesMap = rolesMap;
      }
    });

    this.adminRolesCompanies.getAdminRoles(this.userId).then((res) => {
      this.roles = res.map((r) => r.roleName);
    });

    this.adminRolesCompanies.companiesMap$.subscribe((companyMap: any) => {
      if (companyMap) {
        this.companiesMap = companyMap;
        this.companies = Object.keys(this.companiesMap);
      }
    });
  }

  ngOnInit() {
    this.pageTitle = this.uuid ? 'Edit User' : 'Create User';

    if (this.uuid) {
      this.loadUserData();
    } else {
      this.findCompanyForDropDown();
    }
  }

  loadUserData() {
    this.userService.getUserById(this.uuid).then((result: User) => {
      console.log('Retrieved user: ', result);
      this.user = result;
      if (!!this.user.mobilePhone && this.user.mobilePhone.length > 10) {
        const phoneCode = this.user.mobilePhone
          .split(' ')
          .join('')
          .split('-')
          .join('')
          .split('(')
          .join('')
          .split(')')
          .join('')
          .slice(0, this.user.mobilePhone.length - 10);
        this.defaultMobilePhoneFlag = this.countryFlags[
          this.countryCodes.indexOf(phoneCode)
        ];
      } else {
        this.defaultMobilePhoneFlag = '🇺🇸';
      }
      if (!!this.user.workPhone && this.user.workPhone.length > 10) {
        const phoneCode = this.user.workPhone
          .split(' ')
          .join('')
          .split('-')
          .join('')
          .split('(')
          .join('')
          .split(')')
          .join('')
          .slice(0, this.user.workPhone.length - 10);
        this.defaultWorkPhoneFlag = this.countryFlags[
          this.countryCodes.indexOf(phoneCode)
        ];
      } else {
        this.defaultWorkPhoneFlag = '🇺🇸';
      }
      this.input = {
        selectedRole: this.user.roleName,
        selectedCompany: this.user.companyName,
        emailString: this.user.email,
        fullName: this.user.name,
        jobTitle: this.user.jobTitle,
        workPhoneCode: this.defaultWorkPhoneFlag,
        workPhone: this.intPhonePipe.transform(this.user.workPhone),
        mobilePhoneCode: this.defaultMobilePhoneFlag,
        mobilePhone: this.intPhonePipe.transform(this.user.mobilePhone),
        federated: (this.user.arvFederatedUser === 'true') ? true : false,
      };
    });
  }

  validForm(): boolean {

    let validForm = true;
    this.formError = {
      selectedRole: false,
      selectedCompany: false,
      emailString: false,
      jobTitle: false,
      fullName: false,
      workPhone: false,
      workPhoneValid: false,
      mobilePhone: false,
      mobilePhoneValid: false,
    };

    if (this.input.selectedRole === '') {
      this.formError.selectedRole = true;
      console.error('Missing Role Error');
      validForm = false;
    }

    if (this.input.selectedCompany === '') {
      this.formError.selectedCompany = true;
      console.error('Missing Company Error');
      validForm = false;
    }

    if (this.input.emailString === '') {
      this.formError.emailString = true;
      console.error('Missing Email Error');
      validForm = false;
    } else {
      const emailValidation = customEmailNFValidator(this.input.emailString);
      if (customEmailNFValidator(this.input.emailString)) {
        this.formError.emailString = true;
        console.error('Invalid Email Error');
        validForm = false;
      }
    }

    if (this.input.fullName === '') {
      this.formError.fullName = true;
      console.error('Missing Full Name Error');
      validForm = false;
    } else if (!FIRST_LAST_PATTERN.test(this.input.fullName)) {
      this.formError.fullName = true;
      console.log('Missing First and Last Name Error');
      validForm = false;
    }

    if (this.input.jobTitle === '') {
      this.formError.jobTitle = true;
      console.log('Missing Job Title Error');
      validForm = false;
    }

    if (this.input.workPhone === '') {
      this.formError.workPhone = true;
      console.log('Missing Work Phone Error');
      validForm = false;
    } else {
      this.fullWorkPhone = this.unformatPhone(this.includePhoneCode(this.workPhoneCode, this.input.workPhone));
      if (phoneNumberNFValidator(this.fullWorkPhone) != null) {
        this.formError.workPhoneValid = true;
        console.log('Invalid Work Phone Error');
        validForm = false;
      }
    }

    if (this.input.mobilePhone && this.input.mobilePhone !== '') {
      this.fullMobilePhone = this.unformatPhone(this.includePhoneCode(this.mobilePhoneCode, this.input.mobilePhone));
      if (phoneNumberNFValidator(this.fullMobilePhone) != null) {
        this.formError.mobilePhoneValid = true;
        console.log('Invalid Mobile Phone Error');
        validForm = false;
      }
    }

    return validForm;
  }

  unformatPhone(workablePhoneNUmber: string) {
    let finalPhone = '';

    if (workablePhoneNUmber) {
        finalPhone = workablePhoneNUmber
          .split('')
          .filter((c) => c !== ' ')
          .join('')
          .split('')
          .filter((c) => c !== '-')
          .join('')
          .split('')
          .filter((c) => c !== '(')
          .join('')
          .split('')
          .filter((c) => c !== ')')
          .join('');
      }

    return finalPhone;
  }

  public setWorkPhoneCode(flag) {
    this.workPhoneCode = this.countryCodes[this.countryFlags.indexOf(flag)];
    this.input.workPhoneCode = this.countryCodes[this.countryFlags.indexOf(flag)];
  }

  public setMobilePhoneCode(flag) {
    this.mobilePhoneCode = this.countryCodes[this.countryFlags.indexOf(flag)];
    this.input.mobilePhoneCode = this.countryCodes[this.countryFlags.indexOf(flag)];
  }

  public toggleFederated(event: any) {
    this.input.federated = !this.input.federated;
  }

  public submitForm() {

    this.formIsWaiting = true;
    this.hasUserFormBeenSubmitted = true;
    this.formHasError = false;

    if (!this.validForm()) {
      this.formHasError = true;
      this.hasUserFormBeenSubmitted = false;
      this.formIsWaiting = false;
      return;
    }

    // do data manipulation here, i.e. translate IDs to Strings
    const userData: any = {};

    if (this.canShowCompanies()) {
      userData.companyName = this.input.selectedCompany;
    } else {
      const companyArray = this.companies.filter(entry => {
        if (entry.includes(this.userCompanyId)) {
          return entry;
        }
      });
      if (companyArray && companyArray.length > 0) {
        userData.companyName = companyArray[0];
      }
    }

    userData.roleName = this.input.selectedRole;
    userData.email = this.input.emailString;
    userData.jobTitle = this.input.jobTitle;
    userData.mobilePhone = this.fullMobilePhone;
    userData.workPhone = this.fullWorkPhone;
    userData.name = this.input.fullName;
    userData.arvFederatedUser = this.input.federated;

    if (this.canViewCompanies) {
      userData.companyId = this.companiesMap[userData.companyName];
    } else {
      switch (this.userCompanyId) {
        case 'Arvest':
          userData.companyId = '1';
          break;
        case 'NatGen':
          userData.companyId = '0';
          break;
        case 'Pingora':
          userData.companyId = '20';
          break;
        case 'Lakeview':
          userData.companyId = '18';
          break;
        default:
          break;
      }
    }

    console.log('Company Name: ', userData.companyName, this.userCompanyId, this.companiesMap[this.userCompanyId], this.companiesMap);

    userData.roleId = this.rolesMap[userData.roleName];
    userData.createdUserId = this.userId;

    if (this.uuid) {
      userData.userId = this.uuid;
    }

    let formPromise;
    if (this.uuid) {
      formPromise = this.userService.updateUser(userData);
    } else {
      formPromise = this.userService.createUser(userData);
    }

    // listen to resulting promise/observable
    formPromise
      .then((response: any) => {
        console.log('Form response: ', response);
        if (
          response['newUser'] === false &&
          response['arvGroupMembership'] === 'BPP'
        ) {
          this.userFormMessage = this.messageDisplay.createErrorMessageDisplayWithGivenMessage(
            'This user already exists in Business Partner Portal. Contact your administrator if you need assistance.'
          );
        } else if (
          response['newUser'] === false &&
          response['arvGroupMembership'] !== 'BPP'
        ) {
          // this.userFormMessage =
          // this.messageDisplay.createErrorMessageDisplayWithGivenMessage('Adding users that already exist is not supported.')
          const oldUser = {
            userId: response.uid,
            name: response.givenName + ' ' + response.sn,
            email: response.mail,
            workPhone: response.telephoneNumber,
            mobilePhone: response.mobile,
            arvFederatedUser: response.arvFederatedUser,
            companyName: userData.companyName,
            roleName: userData.roleName,
            roleId: userData.roleId,
            companyId: userData.companyId,
          };
          console.log({ oldUser }, { userData });
          this.compareUsers(oldUser, userData);
          this.userService.getUserById(response.uid).then((res) => {
            res.userId = response.uid;
            this.compareUsers(res, userData);
          });
        } else {
          this.router.navigateByUrl('/admin');
        }
      })
      .catch((error: any) => {
        this.userFormMessage = this.messageDisplay.createErrorMessageDisplayWithGivenMessage(
          'A problem was encountered when creating the user'
        );
        this.formIsWaiting = false;

        console.log('Error saving user to database: ', error);
      });
  }

  compareUsers(existingUser, newUser) {
    const dialogRef = this.dialog.open(CompareUserDialogComponent, {
      data: {
        existingUser,
        newUser,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        newUser.userId = existingUser.userId;
        this.userService
          .updateUser(result)
          .then((res) => this.router.navigateByUrl('/admin'));
        this.dialog.closeAll();
      } else {
        this.router.navigateByUrl('/admin');
        this.dialog.closeAll();
      }
    });
  }

  displayHelpDialog($event) {
    $event.preventDefault();

    this.dialog.open(FederatedHelpDialogComponent);
  }

  displayLockedDialog($event) {
    $event.preventDefault();

    this.dialog.open(LockUserHelpDialogComponent);
  }

  private includePhoneCode(phoneCode: string, phoneNumber: string): string {
    if (phoneNumber && phoneNumber.startsWith(phoneCode)) {
      return phoneNumber;
    } else {
      return `${phoneCode} ${phoneNumber}`;
    }
  }

  ngOnDestroy() {
    this.permissionsSubscription.unsubscribe();
  }

  public lock() {
    this.userService.lockUser(this.uuid).then((res) => {
      console.log(res);
      if (res.StatusMessage !== 'Success') {
        this.userFormMessage = this.messageDisplay.createErrorMessageDisplayWithGivenMessage(
          'A problem was encountered when locking the user.'
        );
      } else {
        this.isLocked = true;
      }
    });
  }

  public unlock() {
    this.userService.unlockUser(this.uuid).then((res) => {
      console.log(res);
      if (res.StatusMessage !== 'Success') {
        this.userFormMessage = this.messageDisplay.createErrorMessageDisplayWithGivenMessage(
          'A problem was encountered when unlocking the user.'
        );
      } else {
        this.isLocked = false;
      }
    });
  }

  workPhoneChange(event: any) {
    if (this.workPhoneCode === '+1') {
      const maskedVal = this.intPhonePipe.transform(this.input.workPhone);
      if (this.input.workPhone !== maskedVal) {
        this.input.workPhone = maskedVal;
      }
    } else {
      const maskedVal = this.noSpaceIntPhonePipe.transform(this.input.workPhone);
      if (this.input.workPhone !== maskedVal) {
        this.input.workPhone = maskedVal;
      }
    }
  }

  mobilePhoneChange(event: any) {
    if (this.mobilePhoneCode === '+1') {
          const maskedVal = this.intPhonePipe.transform(this.input.mobilePhone);
          if (this.input.mobilePhone !== maskedVal) {
            this.input.mobilePhone = maskedVal;
          }
        } else {
          const maskedVal = this.noSpaceIntPhonePipe.transform(this.input.mobilePhone);
          if (this.input.mobilePhone !== maskedVal) {
            this.input.mobilePhone = maskedVal;
          }
        }
  }

  canShowCompanies() {
    if (this.userCompanyId && this.userCompanyId === 'Arvest') {
      return true;
    }

    return false;
  }

  private findCompanyForDropDown() {
    this.userService.getRolesAndCompanies().then((companyRoles: CompanyRole[]) => {
        if (companyRoles && this.userCompanyId) {
          // find the company name from the map
          for (const companyRec of companyRoles) {
            if (companyRec.arvCompanyId === this.userCompanyId) {
              this.input.selectedCompany = companyRec.companyName;
              break;
            }
          }
        }
    });
  }
}
